/**
 * BulkMisPage Component
 *
 * Main page component for Bulk MIS Dashboard
 * Displays metrics, filters, and batch listing for bulk operations monitoring
 */

import React, { useState, useCallback, useMemo } from 'react';
import { BulkMisMetrics } from './BulkMisMetrics';
import { BulkMisUpload } from './BulkMisUpload';
import { BulkMisFiltersComponent } from './BulkMisFilters';
import { BulkBatchRecordsDrawer } from './BulkBatchRecordsDrawer';
import { useBulkMisData } from './hooks/useBulkMisData';
import { useBatchRecords } from './hooks/useBatchRecords';
import { Alert } from '../../ui/Alert';
import { Upload, Eye, RefreshCw } from 'lucide-react';
import { Button, DrawerProvider, Table, type TableColumn, Tag, Loader } from 'equal-ds-ui';
import type { BulkMisFilters, BulkMisPageProps, BulkBatchRecord } from '../../../types/bulk-mis';
import { getProcessingTime } from '../../../utils/time-format';
import { isBulkOrchestrationV2Enabled } from '../../../utils/feature-flags';

export const BulkMisPage: React.FC<BulkMisPageProps> = ({ queryParams }) => {
  const isV2 = isBulkOrchestrationV2Enabled();

  // Filters state
  const [filters, setFilters] = useState<BulkMisFilters>({
    search: '',
    status: 'all',
    dateRange: { from: null, to: null },
    pipelineSource: 'all',
    batchType: isV2 ? (queryParams?.type || 'all') : 'all',
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Upload drawer state
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);

  // Records drawer state
  const [recordsDrawerOpen, setRecordsDrawerOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<BulkBatchRecord | null>(null);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsPageSize, setRecordsPageSize] = useState(25);
  const [recordsStatusFilter, setRecordsStatusFilter] = useState('all');
  const [recordsSearch, setRecordsSearch] = useState('');
  const includeBalances = selectedBatch?.batch_type === 'balance';

  // Fetch batches data
  const { data, loading, error, refresh } = useBulkMisData(filters, page, pageSize);

  // Fetch records for selected batch
  const {
    data: recordsData,
    loading: recordsLoading,
  } = useBatchRecords(
    selectedBatch?.reference_id || '',
    recordsPage,
    recordsPageSize,
    recordsStatusFilter,
    recordsSearch,
    includeBalances
  );

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Get status badge
  const getStatusBadge = (status: BulkBatchRecord['status']) => {
    const statusConfig = {
      COMPLETED: { label: 'Completed', className: 'bg-green-100 text-green-800' },
      PROCESSING: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      FAILED: { label: 'Failed', className: 'bg-red-100 text-red-800' },
      UPLOADED: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      IN_PROGRESS: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      READY: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
    };

    const config = statusConfig[status] || statusConfig.IN_PROGRESS;

    return (
      <Tag className={config.className} size="sm">
        {config.label}
      </Tag>
    );
  };

  // Handlers
  const handleFiltersChange = (newFilters: BulkMisFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page on filter change
  };

  const handleViewRecords = useCallback((batch: BulkBatchRecord) => {
    setSelectedBatch(batch);
    setRecordsDrawerOpen(true);
    setRecordsPage(1);
    setRecordsStatusFilter('all');
    setRecordsSearch('');
  }, []);

  // Table columns
  const columns: TableColumn<BulkBatchRecord>[] = useMemo(() => ([
    {
      key: 'reference_id',
      header: 'Reference ID',
      width: 180,
      accessor: (row: BulkBatchRecord) => row.reference_id,
      render: (_value: string, row: BulkBatchRecord) => (
        <span className="font-mono text-sm text-gray-900" title={row.reference_id || 'N/A'}>
          {row.reference_id
            ? (row.reference_id.length > 20 ? `${row.reference_id.substring(0, 20)}...` : row.reference_id)
            : 'N/A'}
        </span>
      ),
    },
    {
      key: 'file_name',
      header: 'File Name',
      width: 200,
      accessor: (row: BulkBatchRecord) => row.file_name,
      render: (_value: string, row: BulkBatchRecord) => (
        <span className="text-sm text-gray-900" title={row.file_name}>
          {row.file_name.length > 30
            ? `${row.file_name.substring(0, 30)}...`
            : row.file_name}
        </span>
      ),
    },
    isV2
      ? {
          key: 'triggered_by',
          header: 'Triggered By',
          width: 160,
          accessor: (row: BulkBatchRecord) => row.triggered_by || row.pipeline_source || 'API',
          render: (_value: string, row: BulkBatchRecord) => (
            <span className="text-sm text-gray-700">{row.triggered_by || row.pipeline_source || 'API'}</span>
          ),
        }
      : {
          key: 'pipeline_source',
          header: 'Source',
          width: 100,
          accessor: (row: BulkBatchRecord) => row.pipeline_source,
          render: (value: string) => (
            <span className="text-sm text-gray-700 capitalize">{value}</span>
          ),
        },
    isV2 ? {
      key: 'batch_type',
      header: 'Type',
      width: 180,
      accessor: (row: BulkBatchRecord) => row.batch_type || '',
      render: (_value: string, row: BulkBatchRecord) => {
        if (row.batch_type === 'loan_monitoring') {
          return <Tag className="bg-blue-100 text-blue-800 whitespace-nowrap" size="sm">Loan Monitoring</Tag>;
        }
        if (row.batch_type === 'balance') {
          return <Tag className="bg-purple-100 text-purple-800" size="sm">Balance</Tag>;
        }
        return <Tag className="bg-gray-100 text-gray-600" size="sm">N/A</Tag>;
      },
    } : null,
    {
      key: 'status',
      header: 'Status',
      width: 140,
      accessor: (row: BulkBatchRecord) => row.status,
      render: (_value: string, row: BulkBatchRecord) => getStatusBadge(row.status),
    },
    {
      key: 'total_records',
      header: 'Records',
      width: 120,
      accessor: (row: BulkBatchRecord) => row.total_records,
      render: (_value: number, row: BulkBatchRecord) => (
        <span className="text-sm text-gray-700">{row.total_records.toLocaleString()}</span>
      ),
    },
    isV2 ? {
      key: 'processing_time',
      header: 'Processing Time',
      width: 140,
      accessor: (row: BulkBatchRecord) => getProcessingTime(row),
      render: (_value: string, row: BulkBatchRecord) => (
        <span className="text-sm text-gray-700">{getProcessingTime(row)}</span>
      ),
    } : null,
    {
      key: 'created_at',
      header: 'Created At',
      width: 180,
      accessor: (row: BulkBatchRecord) => row.created_at,
      render: (_value: string, row: BulkBatchRecord) => (
        <span className="text-sm text-gray-600">{formatDate(row.created_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      isActions: true,
      width: isV2 ? 140 : 80,
      align: 'center',
      accessor: (row: BulkBatchRecord) => row.batch_id || row.reference_id,
      render: (_value: string, row: BulkBatchRecord) => isV2 ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewRecords(row);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 rounded-lg transition-colors"
          title="View Data"
          aria-label="View Data"
        >
          <Eye className="w-3.5 h-3.5" />
          View Data
        </button>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewRecords(row);
          }}
          className="inline-flex items-center justify-center p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          title="View Records"
          aria-label="View Records"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ] as (TableColumn<BulkBatchRecord> | null)[]).filter(Boolean) as TableColumn<BulkBatchRecord>[], [handleViewRecords, isV2]);

  return (
    <div className="equal-ds-root p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Bulk MIS Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Monitor and analyze bulk operation performance metrics
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Metrics Section */}
      {data?.summary && <BulkMisMetrics summary={data.summary} />}

      {/* Upload Drawer */}
      <DrawerProvider open={uploadDrawerOpen} onOpenChange={setUploadDrawerOpen} side="right">
        <BulkMisUpload
          isOpen={uploadDrawerOpen}
          onClose={() => setUploadDrawerOpen(false)}
          onUploadSuccess={refresh}
        />
      </DrawerProvider>

      {/* Filters Section */}
      <BulkMisFiltersComponent
        filters={filters}
        onFiltersChange={handleFiltersChange}
        screenName="data_fetch"
      />

      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Table Header with Upload and Export Buttons */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">All Batches</h2>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setUploadDrawerOpen(true)}
              variant="primary"
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload Batch
            </Button>
            <Button
              onClick={refresh}
              variant="secondary"
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Table */}
        {data && (
          <Table
            data={data.batches}
            columns={columns}
            loading={loading}
            onRowClick={handleViewRecords}
            pagination={{
              currentPage: page,
              pageSize: pageSize,
              totalPages: data.pagination.total_pages,
              totalItems: data.pagination.total_records,
              onPageChange: (newPage) => setPage(newPage),
              onPageSizeChange: (newSize) => {
                setPageSize(newSize);
                setPage(1);
              },
              pageSizeOptions: [25, 50, 100],
            }}
          />
        )}

        {/* Loading State */}
        {loading && !data && (
          <div className="p-12">
            <div className="flex flex-col items-center justify-center gap-4">
              <Loader cellSize={12} />
              <p className="text-gray-600">Loading batches...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && data && data.batches.length === 0 && (
          <div className="p-12 text-center">
            <p className="text-gray-500">No batches found</p>
          </div>
        )}
      </div>

      {/* Records Drawer */}
      {selectedBatch && (
        <DrawerProvider open={recordsDrawerOpen} onOpenChange={setRecordsDrawerOpen} side="right">
          <BulkBatchRecordsDrawer
            isOpen={recordsDrawerOpen}
            onClose={() => {
              setRecordsDrawerOpen(false);
              setSelectedBatch(null);
            }}
            batch={selectedBatch}
            records={recordsData?.records || []}
            pagination={recordsData?.pagination || { page: 1, limit: 25, total_records: 0, total_pages: 0, has_next: false, has_prev: false }}
            loading={recordsLoading}
            summary={recordsData?.summary || undefined}
            screenName="data_fetch"
            onPageChange={setRecordsPage}
            onPageSizeChange={(newSize) => {
              setRecordsPageSize(newSize);
              setRecordsPage(1);
            }}
            onStatusFilterChange={setRecordsStatusFilter}
            onSearchChange={setRecordsSearch}
          />
        </DrawerProvider>
      )}

    </div>
  );
};
